/**
 * Live PixiJS/WebGL compositor — promoted from the Spike A harness. Composites
 * the EDL at a given timeline frame onto its own mounted canvas: active video
 * clip (crop+scale on the GPU) + active caption text layers.
 *
 * Parity contract (Phase 0): geometry matches the ffmpeg export; captions are an
 * approximation of libass (see docs/track2-phase0-results.md §A). Transitions/SFX
 * are not previewed in the Phase 1 MVP.
 */
import {
  Application,
  BlurFilter,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  TextStyle,
  Texture,
} from 'pixi.js';
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  type CaptionItem,
  type CaptionLayer,
  type ClipFit,
  type ClipItem,
  type CropRect,
  type Edl,
  type GifItem,
  type LayoutSlots,
  type TitleOverlay,
  type TransitionItem,
  BRAND_WINDOW_SEC,
  CTA_FONT_SIZE_PX,
  CTA_WINDOW_SEC,
  TITLE_WINDOW_SEC,
  captionLayerOf,
  computeLayoutSlots,
  framesToSec,
  itemEnd,
  overlayCaptionMarginV,
} from '@repo/editor-core';
import { MANAGED_FONT_FAMILIES } from '@/editor/fonts';
import { DEFAULT_BRANDING, PLATFORM_LOGOS, type ShortsBranding } from '@/editor/branding';
import type { DecoderPool } from './decoder-pool';

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

function clipAt(edl: Edl, frame: number): ClipItem | null {
  const track = edl.tracks.find((t) => t.kind === 'video');
  for (const item of track?.items ?? []) {
    if (item.type === 'clip' && frame >= item.from && frame < itemEnd(item)) return item;
  }
  return null;
}

/**
 * 활성 자막 — 모든 caption 트랙에서 모은다. `find`로 첫 트랙만 읽으면 개입
 * 자막(caption-2)이 프리뷰에서 조용히 사라져 익스포트와 어긋난다(parity 계약).
 */
function captionsAt(edl: Edl, frame: number): (CaptionItem & { layer: CaptionLayer })[] {
  return edl.tracks
    .filter((t) => t.kind === 'caption')
    .flatMap((t) =>
      t.items
        .filter(
          (i): i is CaptionItem => i.type === 'caption' && frame >= i.from && frame < itemEnd(i)
        )
        .map((i) => ({ ...i, layer: captionLayerOf(t.id) }))
    );
}

/** Active B-roll overlay clips at a frame (composited over the main video). */
function brollAt(edl: Edl, frame: number): ClipItem[] {
  const track = edl.tracks.find((t) => t.kind === 'broll');
  return (track?.items ?? []).filter(
    (i): i is ClipItem => i.type === 'clip' && frame >= i.from && frame < itemEnd(i)
  );
}

/** Active GIF overlays at a frame (composited on top of video, under captions). */
function gifsAt(edl: Edl, frame: number): GifItem[] {
  return edl.tracks
    .filter((t) => t.kind === 'overlay')
    .flatMap((t) => t.items)
    .filter((i): i is GifItem => i.type === 'gif' && frame >= i.from && frame < itemEnd(i));
}

/** The active transition (and its two clips) at a frame, if any. */
function transitionAt(
  edl: Edl,
  frame: number
): { transition: TransitionItem; after: ClipItem; before: ClipItem } | null {
  const track = edl.tracks.find((t) => t.kind === 'video');
  for (const item of track?.items ?? []) {
    if (item.type !== 'transition' || frame < item.from || frame >= itemEnd(item)) continue;
    const after = track?.items.find((i) => i.id === item.afterItemId);
    const before = track?.items.find((i) => i.id === item.beforeItemId);
    if (after?.type === 'clip' && before?.type === 'clip') {
      return { transition: item, after, before };
    }
  }
  return null;
}

export class Compositor {
  private app = new Application();
  private fps: number;
  /** Monotonic render token — stale async renders are dropped. */
  private renderSeq = 0;
  /** Common branding (logo/channel/CTA). Defaults to the Aether.fun preset. */
  private branding: ShortsBranding = DEFAULT_BRANDING;

  constructor(fps = 30) {
    this.fps = fps;
  }

  /** Swap the branding overlay data (per-streamer common settings). */
  setBranding(branding: ShortsBranding): void {
    this.branding = branding;
  }

  async init(): Promise<void> {
    await this.app.init({
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      background: 0x000000,
      antialias: false,
      preference: 'webgl',
      autoStart: false,
    });
  }

  /** The DOM canvas to mount into the preview pane. */
  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  /**
   * Render the EDL at `frame`. Async (decodes the source frame). If a newer
   * render starts before this one finishes drawing, this one is dropped so the
   * canvas always reflects the latest requested frame.
   */
  async renderAt(edl: Edl, frame: number, pool: DecoderPool): Promise<void> {
    const seq = ++this.renderSeq;
    const base = clipAt(edl, frame);
    const captions = captionsAt(edl, frame);
    const slots = computeLayoutSlots(edl.layout);

    const stage = new Container();
    const tr = transitionAt(edl, frame);
    if (tr && tr.transition.kind !== 'cut') {
      // Inside a transition window: render outgoing (A) + incoming (B) per kind.
      const t = clamp01((frame - tr.transition.from) / tr.transition.durationInFrames);
      const aD = await this.decodeClipFrame(pool, tr.after, frame, seq);
      if (!aD) return;
      const bD = await this.decodeClipFrame(pool, tr.before, frame, seq);
      if (!bD) return;
      for (const sp of this.transitionSprites(tr.transition.kind, t, aD, bD, tr.after, tr.before)) {
        stage.addChild(sp);
      }
    } else if (base) {
      const d = await this.decodeClipFrame(pool, base, frame, seq);
      if (!d) return; // superseded
      // The layout preset decides both WHERE the video sits (video slot rect) and
      // its default fit; an explicit clip.fit still wins.
      const box = slots.video.rect;
      const fit = base.fit ?? slots.video.fit;
      // 'blur' fit: a blurred, slightly-overscanned cover copy sits behind the
      // aspect-preserved foreground (matches the ffmpeg boxblur export).
      if (fit === 'blur') {
        const bg = this.clipSprite(d.bitmap, d.srcW, d.srcH, base, {
          forceCover: true,
          scale: 1.12,
          box,
          fitOverride: 'blur',
        });
        bg.filters = [new BlurFilter({ strength: 40, quality: 4 })];
        stage.addChild(bg);
      }
      stage.addChild(this.clipSprite(d.bitmap, d.srcW, d.srcH, base, { box, fitOverride: fit }));
    }

    // B-roll overlays on top of the main video (PIP via rect, or full cutaway).
    for (const bc of brollAt(edl, frame)) {
      const bd = await this.decodeClipFrame(pool, bc, frame, seq);
      if (!bd) return;
      stage.addChild(this.clipSprite(bd.bitmap, bd.srcW, bd.srcH, bc, { rect: bc.rect }));
    }

    // GIF/meme overlays, placed by rect (aspect preserved), under captions.
    for (const gif of gifsAt(edl, frame)) {
      const gd = await this.decodeGifFrame(pool, gif, frame, seq);
      if (gd === 'superseded') return;
      if (gd) stage.addChild(this.gifSprite(gd.bitmap, gd.srcW, gd.srcH, gif));
    }

    // Ensure each caption's webfont (and its exact glyphs) is loaded before we
    // rasterize text — otherwise Pixi draws a fallback and the canvas won't
    // auto-redraw. document.fonts caches, so this is ~free once warm.
    for (const caption of captions) {
      const fam = caption.style.fontFamily;
      if (!MANAGED_FONT_FAMILIES.has(fam)) continue;
      const spec = `${caption.style.fontSizePx}px "${fam}"`;
      if (!document.fonts.check(spec, caption.text)) {
        try {
          await document.fonts.load(spec, caption.text);
        } catch {
          /* missing/blocked font — fall back to canvas default */
        }
        if (seq !== this.renderSeq) return;
      }
    }

    // Branding + title overlays, positioned by the layout preset. Drawn above the
    // video/overlays but below captions (subtitles sit on top of everything).
    for (const node of this.brandingNodes(edl, slots, frame)) stage.addChild(node);

    for (const caption of captions)
      stage.addChild(this.captionText(caption, slots.subtitle, caption.layer));

    this.app.renderer.render(stage);
    // Destroy only the display objects — NOT the texture sources: frame textures
    // are bound to decoder-cached ImageBitmaps and reused across renders.
    stage.destroy({ children: true });
  }

  /** Decode a clip's frame at a timeline position, clamped to the clip's range. */
  private async decodeClipFrame(
    pool: DecoderPool,
    clip: ClipItem,
    timelineFrame: number,
    seq: number
  ): Promise<{ bitmap: ImageBitmap; srcW: number; srcH: number } | null> {
    const clamped = Math.max(clip.from, Math.min(timelineFrame, itemEnd(clip) - 1));
    const sourceFrame = Math.round(
      (clip.source.sourceInSec + framesToSec(clamped - clip.from)) * this.fps
    );
    const decoded = await pool.frameAt(clip.source.assetId, Math.max(0, sourceFrame));
    if (seq !== this.renderSeq) return null;
    const dims = pool.dimensions(clip.source.assetId);
    if (dims.width === 0 || dims.height === 0) return null;
    return { bitmap: decoded.bitmap, srcW: dims.width, srcH: dims.height };
  }

  private clipSprite(
    bitmap: ImageBitmap,
    srcW: number,
    srcH: number,
    clip: ClipItem,
    opts?: {
      alpha?: number;
      offsetX?: number;
      scale?: number;
      rect?: CropRect;
      /** Force cover-fill regardless of `clip.fit` (used for the blur backdrop). */
      forceCover?: boolean;
      /** Target region on the canvas to fit into (0..1). Absent → full canvas. */
      box?: CropRect;
      /** Fit to use instead of `clip.fit` (layout preset picks the video fit). */
      fitOverride?: ClipFit;
    }
  ): Sprite {
    const source = Texture.from(bitmap).source;
    const crop = clip.crop ?? { x: 0, y: 0, w: 1, h: 1 };
    const frame = new Rectangle(
      Math.round(crop.x * srcW),
      Math.round(crop.y * srcH),
      Math.round(crop.w * srcW),
      Math.round(crop.h * srcH)
    );
    const sprite = new Sprite(new Texture({ source, frame }));
    if (opts?.rect) {
      // Overlay placement on the canvas (B-roll).
      sprite.position.set(opts.rect.x * CANVAS_WIDTH, opts.rect.y * CANVAS_HEIGHT);
      sprite.width = opts.rect.w * CANVAS_WIDTH;
      sprite.height = opts.rect.h * CANVAS_HEIGHT;
    } else {
      // Fit the (cropped) source into a target box (default: the full canvas)
      // preserving aspect — no stretch. `cover` fills the box (overflow past the
      // canvas is clipped by the fixed framebuffer); `contain`/`blur` fit inside.
      const box = opts?.box ?? { x: 0, y: 0, w: 1, h: 1 };
      const boxX = box.x * CANVAS_WIDTH;
      const boxY = box.y * CANVAS_HEIGHT;
      const boxW = box.w * CANVAS_WIDTH;
      const boxH = box.h * CANVAS_HEIGHT;
      const fit = opts?.fitOverride ?? clip.fit ?? 'blur';
      const fillCover = opts?.forceCover === true || fit === 'cover';
      const contentAR = frame.width / frame.height;
      const boxAR = boxW / boxH;
      const s = fillCover
        ? contentAR > boxAR
          ? boxH / frame.height
          : boxW / frame.width
        : contentAR > boxAR
          ? boxW / frame.width
          : boxH / frame.height;
      const scale = opts?.scale ?? 1;
      sprite.width = frame.width * s * scale;
      sprite.height = frame.height * s * scale;
      // Center within the box, then apply any horizontal slide offset (transitions).
      sprite.position.set(
        boxX + (boxW - sprite.width) / 2 + (opts?.offsetX ?? 0),
        boxY + (boxH - sprite.height) / 2
      );
    }
    if (opts?.alpha !== undefined) sprite.alpha = opts.alpha;
    return sprite;
  }

  /** Decode a GIF overlay's frame at a timeline position (relative to its start). */
  private async decodeGifFrame(
    pool: DecoderPool,
    gif: GifItem,
    timelineFrame: number,
    seq: number
  ): Promise<{ bitmap: ImageBitmap; srcW: number; srcH: number } | null | 'superseded'> {
    let decoded;
    try {
      // Loop the GIF over its (possibly longer) block: map to its natural length.
      const rel = Math.max(0, timelineFrame - gif.from);
      const n = await pool.frameCount(gif.src); // 0 until opened → no modulo yet
      decoded = await pool.frameAt(gif.src, n > 0 ? rel % n : rel);
    } catch {
      return null; // a single bad gif must not blank the whole preview
    }
    if (seq !== this.renderSeq) return 'superseded';
    const dims = pool.dimensions(gif.src);
    if (dims.width === 0 || dims.height === 0) return null;
    return { bitmap: decoded.bitmap, srcW: dims.width, srcH: dims.height };
  }

  /** A GIF sprite fitted inside its `rect` box (aspect preserved), rect-anchored. */
  private gifSprite(bitmap: ImageBitmap, srcW: number, srcH: number, gif: GifItem): Sprite {
    const sprite = new Sprite(new Texture({ source: Texture.from(bitmap).source }));
    const boxW = gif.rect.w * CANVAS_WIDTH;
    const boxH = gif.rect.h * CANVAS_HEIGHT;
    const s = Math.min(boxW / srcW, boxH / srcH);
    sprite.width = srcW * s;
    sprite.height = srcH * s;
    sprite.position.set(gif.rect.x * CANVAS_WIDTH, gif.rect.y * CANVAS_HEIGHT);
    return sprite;
  }

  /** Sprites for a transition window, per kind (A = outgoing, B = incoming). */
  private transitionSprites(
    kind: TransitionItem['kind'],
    t: number,
    aD: { bitmap: ImageBitmap; srcW: number; srcH: number },
    bD: { bitmap: ImageBitmap; srcW: number; srcH: number },
    aClip: ClipItem,
    bClip: ClipItem
  ): Sprite[] {
    const A = (o?: Parameters<Compositor['clipSprite']>[4]) =>
      this.clipSprite(aD.bitmap, aD.srcW, aD.srcH, aClip, o);
    const B = (o?: Parameters<Compositor['clipSprite']>[4]) =>
      this.clipSprite(bD.bitmap, bD.srcW, bD.srcH, bClip, o);
    switch (kind) {
      case 'whip': // B slides in from the right as A slides out left.
        return [A({ offsetX: -t * CANVAS_WIDTH }), B({ offsetX: (1 - t) * CANVAS_WIDTH })];
      case 'zoom': // B zooms/settles in over A.
        return [A(), B({ alpha: t, scale: 1.3 - 0.3 * t })];
      case 'fade':
      default: // linear crossfade: A*(1-t) + B*t
        return [A(), B({ alpha: t })];
    }
  }

  private captionText(
    caption: CaptionItem,
    subtitle: LayoutSlots['subtitle'],
    layer: CaptionLayer = 'speech'
  ): Text {
    const s = caption.style;
    const style = new TextStyle({
      fontFamily: s.fontFamily,
      fontSize: s.fontSizePx,
      fontWeight: s.bold ? '700' : '400',
      fill: s.primaryColor,
      stroke: { color: s.outlineColor, width: s.outlineWidth, join: 'round' },
      align: 'center',
    });
    const text = new Text({ text: caption.text, style });
    // The layout preset owns WHERE the subtitle sits (so it never collides with
    // branding/CTA); the caption's own style still owns how it LOOKS.
    // 개입 자막(caption-2)은 발화 자막 한 줄 위. 렌더러와 **같은 함수**를 쓴다 —
    // 각자 계산하면 프리뷰와 익스포트에서 자막 위치가 갈린다(parity 계약).
    const marginV = layer === 'overlay' ? overlayCaptionMarginV(subtitle) : subtitle.marginVPx;
    if (subtitle.alignment === 'top') {
      text.anchor.set(0.5, 0);
      text.position.set(CANVAS_WIDTH / 2, marginV);
    } else if (subtitle.alignment === 'center') {
      text.anchor.set(0.5, 0.5);
      text.position.set(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
    } else {
      text.anchor.set(0.5, 1);
      text.position.set(CANVAS_WIDTH / 2, CANVAS_HEIGHT - marginV);
    }
    return text;
  }

  // ── Branding / title overlays ────────────────────────────────

  /**
   * Build the layout's branding overlays: logo, channel name, title, CTA.
   *
   * 노출 구간은 렌더러(render.ts)와 **동일해야** 한다 — 브랜딩 로고·채널명은 도입
   * 2.5초, 타이틀은 3초, CTA는 마지막 3초. 전 구간에 띄우면 영상 위를 텍스트가 계속
   * 덮는다. 여기서 창을 다르게 잡으면 프리뷰와 결과물이 갈린다(parity 계약).
   */
  private brandingNodes(edl: Edl, slots: LayoutSlots, frame: number): Container[] {
    const nodes: Container[] = [];
    const totalSec = framesToSec(edl.durationInFrames);
    const sec = framesToSec(frame);
    const inBrand = sec <= Math.min(BRAND_WINDOW_SEC, totalSec);
    const inTitle = sec <= Math.min(TITLE_WINDOW_SEC, totalSec);
    const inCta = sec >= Math.max(0, totalSec - CTA_WINDOW_SEC);

    if (inBrand) {
      nodes.push(this.logoBadge(slots.logo));
      if (this.branding.channelName) {
        nodes.push(
          this.overlayLabel(this.branding.channelName, slots.channelName, {
            fontSizePx: Math.round(slots.channelName.h * CANVAS_HEIGHT * 0.52),
            align: 'left',
          })
        );
      }
    }
    if (inTitle && edl.title?.text) nodes.push(this.titleText(edl.title, slots.title));
    if (inCta) nodes.push(this.ctaBadge(slots.cta));
    return nodes;
  }

  /** Platform logo: a rounded badge in the platform color + its glyph. */
  private logoBadge(slot: CropRect): Container {
    const c = new Container();
    const x = slot.x * CANVAS_WIDTH;
    const y = slot.y * CANVAS_HEIGHT;
    const w = slot.w * CANVAS_WIDTH;
    const h = slot.h * CANVAS_HEIGHT;
    const size = Math.min(w, h);
    const oy = y + (h - size) / 2;
    const logo = PLATFORM_LOGOS[this.branding.platform];
    const badge = new Graphics();
    badge.roundRect(x, oy, size, size, size * 0.24).fill(logo.color);
    c.addChild(badge);
    const glyph = new Graphics();
    // fill-rule 은 «구멍 뚫린» 글리프(씨미의 두 눈)에만 붙는다. 빼면 통짜 실루엣이 된다.
    const rule = logo.fillRule ? ` fill-rule="${logo.fillRule}" clip-rule="${logo.fillRule}"` : '';
    glyph.svg(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="${logo.path}" fill="${logo.fg}"${rule}/></svg>`
    );
    const gscale = (size * 0.62) / 24;
    glyph.scale.set(gscale);
    glyph.position.set(x + (size - 24 * gscale) / 2, oy + (size - 24 * gscale) / 2);
    c.addChild(glyph);
    return c;
  }

  /** A styled text label placed inside a slot box (left-aligned, v-centered). */
  private overlayLabel(
    text: string,
    slot: CropRect,
    opts: { fontSizePx: number; align: 'left' | 'center' }
  ): Text {
    const style = new TextStyle({
      fontFamily: 'Pretendard',
      fontSize: opts.fontSizePx,
      fontWeight: '700',
      fill: '#FFFFFF',
      stroke: {
        color: '#000000',
        width: Math.max(2, Math.round(opts.fontSizePx * 0.08)),
        join: 'round',
      },
      align: opts.align,
      wordWrap: true,
      wordWrapWidth: slot.w * CANVAS_WIDTH,
      breakWords: true,
    });
    const t = new Text({ text, style });
    const x = slot.x * CANVAS_WIDTH;
    const y = slot.y * CANVAS_HEIGHT;
    const w = slot.w * CANVAS_WIDTH;
    const h = slot.h * CANVAS_HEIGHT;
    if (opts.align === 'center') {
      t.anchor.set(0.5, 0.5);
      t.position.set(x + w / 2, y + h / 2);
    } else {
      t.anchor.set(0, 0.5);
      t.position.set(x, y + h / 2);
    }
    return t;
  }

  /** Project title, centered in its slot, wrapping to the slot width. */
  private titleText(title: TitleOverlay, slot: CropRect): Text {
    const s = title.style;
    const style = new TextStyle({
      fontFamily: s.fontFamily,
      fontSize: s.fontSizePx,
      fontWeight: s.bold ? '700' : '400',
      fill: s.primaryColor,
      stroke: { color: s.outlineColor, width: s.outlineWidth, join: 'round' },
      align: 'center',
      wordWrap: true,
      wordWrapWidth: slot.w * CANVAS_WIDTH,
      breakWords: true,
    });
    const t = new Text({ text: title.text, style });
    t.anchor.set(0.5, 0.5);
    t.position.set((slot.x + slot.w / 2) * CANVAS_WIDTH, (slot.y + slot.h / 2) * CANVAS_HEIGHT);
    return t;
  }

  /** CTA label, centered in its slot. Mirrors the renderer's `an=5` overlay. */
  private ctaBadge(slot: CropRect): Text {
    const label = new Text({
      text: this.branding.ctaText,
      style: new TextStyle({
        fontFamily: 'Pretendard',
        // 슬롯 높이에 비례시키지 않는다 — 상자를 옮기려고 줄이면 글자까지 작아진다.
        fontSize: CTA_FONT_SIZE_PX,
        fontWeight: '700',
        fill: '#FFFFFF',
        stroke: { color: '#000000', width: 4, join: 'round' },
        align: 'center',
        // 한글 CTA 는 「yoy.live」보다 훨씬 넓다 — 상자 밖으로 나가지 않게 접는다.
        wordWrap: true,
        wordWrapWidth: slot.w * CANVAS_WIDTH,
        breakWords: true,
      }),
    });
    label.anchor.set(0.5, 0.5);
    label.position.set((slot.x + slot.w / 2) * CANVAS_WIDTH, (slot.y + slot.h / 2) * CANVAS_HEIGHT);
    return label;
  }

  dispose(): void {
    /*
     * 🔴 **텍스처 소스까지 놓아야 한다.** `destroy(true)` 는 renderer 와 canvas 만
     * 정리하고 스테이지가 쥐고 있던 텍스처는 그대로 둔다. 프레임마다
     * `Texture.from(bitmap)` 으로 GPU 텍스처를 만드는 구조라(렌더 뒤에는 표시
     * 객체만 버리고 소스는 재사용한다), 에디터를 닫았다 열 때마다 GPU 쪽에
     * 텍스처가 쌓인다. JS 힙은 20~27MB 로 멀쩡한데 **탭이 죽는다** —
     * 실측 2026-09-11: 열기→재생→레이아웃 전환→모달을 반복하니 7번째에
     * `Page crashed` 가 났다(힙은 끝까지 27MB).
     */
    this.app.destroy(true, { children: true, texture: true, textureSource: true });
  }
}
